import React, { useState } from "react";
import axios from "axios";
import Joi from "joi";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Alert,
} from "@mui/material";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import { useDialog } from "../../context/DialogContext";

/** Robust Joi Validation Schema */
export const accountValidation = Joi.object({
  customerId: Joi.string()
    .trim()
    .required()
    .max(50)
    .pattern(/^[a-zA-Z0-9\s_-]+$/)
    .messages({
      "string.empty": "Customer ID is required",
      "string.pattern.base": "Customer ID contains invalid characters",
      "string.max": "Customer ID cannot exceed 50 characters",
    }),

  domain: Joi.string()
    .trim()
    .required()
    .max(100)
    .pattern(/^[a-zA-Z0-9.-]+$/)
    .messages({
      "string.empty": "Domain is required",
      "string.pattern.base": "Invalid domain format",
      "string.max": "Domain cannot exceed 100 characters",
    }),

  status: Joi.string()
    .trim()
    .required()
    .valid("active", "inactive", "suspended")
    .messages({
      "any.required": "Status is required",
      "any.only": "Invalid status value",
    }),

  phoneNumberId: Joi.string()
    .trim()
    .required()
    .max(20)
    .pattern(/^[0-9]+$/)
    .messages({
      "string.empty": "Phone Number ID is required",
      "string.pattern.base": "Must contain only numbers",
      "string.max": "Phone Number ID cannot exceed 20 digits",
    }),

  wabaToken: Joi.string().trim().allow("").max(255).messages({
    "string.max": "WABA Token cannot exceed 255 characters",
  }),

  wabaAccountId: Joi.string()
    .trim()
    .required()
    .max(20)
    .pattern(/^[0-9]+$/)
    .messages({
      "string.empty": "WABA Account ID is required",
      "string.pattern.base": "Must contain only numbers",
      "string.max": "WABA Account ID cannot exceed 20 digits",
    }),

  appId: Joi.string()
    .trim()
    .required()
    .max(50)
    .pattern(/^[a-zA-Z0-9_-]+$/)
    .messages({
      "string.empty": "App ID is required",
      "string.pattern.base":
        "Only letters, numbers, hyphens and underscores allowed",
      "string.max": "App ID cannot exceed 50 characters",
    }),

  businessCategory: Joi.string()
    .trim()
    .required()
    .valid(
      "E-commerce / Retail",
      "Healthcare",
      "Travel / Hospitality",
      "Finance / Banking / Insurance",
      "Education / Edtech",
      "Real Estate",
      "Utilities / Telecom, others",
      "Food / Restaurants",
      "Others"
    )
    .messages({
      "string.empty": "Business Category is required",
      "any.only": "Invalid Business Category",
    }),

  customCategory: Joi.when("businessCategory", {
    is: "Others",
    then: Joi.string()
      .trim()
      .required()
      .max(50)
      .pattern(/^[a-zA-Z0-9\s_-]+$/)
      .messages({
        "string.empty": "Custom Category is required when 'Others' is selected",
        "string.pattern.base": "Custom Category contains invalid characters",
        "string.max": "Custom Category cannot exceed 50 characters",
      }),
    otherwise: Joi.string().allow("").optional(),
  }),
});

/** Centralized initial state */
const initialForm = {
  customerId: "",
  domain: "",
  status: "active",
  phoneNumberId: "",
  wabaToken: "",
  wabaAccountId: "",
  appId: "",
  businessCategory: "",
  customCategory: "",
};

const AddAccount = () => {
  const { showDialog } = useDialog();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };

  const validate = () => {
    const { error } = accountValidation.validate(form, { abortEarly: false });
    if (!error) return true;

    const newErrors = {};
    error.details.forEach((detail) => {
      const field = detail.path[0];
      newErrors[field] = detail.message;
    });
    setErrors(newErrors);
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Local Validation Check using showDialog
    if (!validate()) {
      showDialog(
        "Validation Error",
        "Please fix the highlighted errors in the form before submitting.",
        {
          type: "error",
          onConfirm: async () => {}, // do nothing
        }
      );
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_ENDPOINT}/root/add-account`, form, getHeader());

      // Success keeps using Snackbar for non-blocking UX
      setSnack({
        open: true,
        msg: "Account added successfully!",
        severity: "success",
      });

      setForm(initialForm);
      setErrors({});
    } catch (err) {
      // 2. Extract specific backend errors
      const errorData = err.response?.data;
      let title = "Error Saving Account";
      let description =
        errorData?.message ||
        err.message ||
        "Something went wrong while saving.";

      // If backend sends an array of Joi errors, format them nicely
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        description = `${description}\n\n${errorData.errors.join("\n")}`;
      }

      // 3. API Error Check using showDialog
      showDialog(title, description, {
        type: "error",
        onConfirm: async () => {}, // do nothing
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container sx={{ my: 3 }}>
      <BackBtn />
      <Container maxWidth="md">
        <PageTitle sx={{ mt: 1 }}>Add Account</PageTitle>
        <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Customer ID */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Customer ID / Name"
                  name="customerId"
                  value={form.customerId}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.customerId)}
                  helperText={errors.customerId}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>

              {/* Domain */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Domain"
                  name="domain"
                  value={form.domain}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.domain)}
                  helperText={errors.domain || "e.g., example.com"}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Status"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.status)}
                  helperText={errors.status}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </TextField>
              </Grid>

              {/* Phone Number ID */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number ID"
                  name="phoneNumberId"
                  value={form.phoneNumberId}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.phoneNumberId)}
                  helperText={errors.phoneNumberId}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>

              {/* WABA Token */}
              <Grid item xs={12}>
                <TextField
                  label="WABA Token"
                  name="wabaToken"
                  value={form.wabaToken}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={Boolean(errors.wabaToken)}
                  helperText={
                    errors.wabaToken || "can leave blank to supply later"
                  }
                  inputProps={{ maxLength: 255 }}
                />
              </Grid>

              {/* WABA Account ID */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="WABA Account ID"
                  name="wabaAccountId"
                  value={form.wabaAccountId}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.wabaAccountId)}
                  helperText={errors.wabaAccountId}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>

              {/* App ID */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="App ID"
                  name="appId"
                  value={form.appId}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.appId)}
                  helperText={errors.appId}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>

              {/* Business Category */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Business Category"
                  name="businessCategory"
                  value={form.businessCategory}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.businessCategory)}
                  helperText={errors.businessCategory}
                >
                  <MenuItem value="E-commerce / Retail">
                    E-commerce / Retail
                  </MenuItem>
                  <MenuItem value="Healthcare">Healthcare</MenuItem>
                  <MenuItem value="Travel / Hospitality">
                    Travel / Hospitality
                  </MenuItem>
                  <MenuItem value="Finance / Banking / Insurance">
                    Finance / Banking / Insurance
                  </MenuItem>
                  <MenuItem value="Education / Edtech">
                    Education / Edtech
                  </MenuItem>
                  <MenuItem value="Real Estate">Real Estate</MenuItem>
                  <MenuItem value="Utilities / Telecom, others">
                    Utilities / Telecom, others
                  </MenuItem>
                  <MenuItem value="Food / Restaurants">
                    Food / Restaurants
                  </MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </TextField>
              </Grid>

              {/* Custom Category (only if "Others") */}
              {form.businessCategory === "Others" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Enter Custom Category"
                    name="customCategory"
                    value={form.customCategory}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                    error={Boolean(errors.customCategory)}
                    helperText={errors.customCategory}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
              )}

              <Grid item xs={12} sx={{ textAlign: "right" }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={18} />}
                >
                  {submitting ? "Saving…" : "Save Account"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddAccount;
